import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { AuthService } from '@features/auth/services/auth-service';
import { ProfileService } from './profile-service';
import { TableSession } from '@core/models/table-session.model';
import { TableSessionInfo } from '@features/client/models/response/table-session-info.model';
import { SessionUtils } from '@core/utils/session-utils';
import { ServerSentEventsService } from '@core/services/server-sent-events.service';
import { Observable, Subscription, tap } from 'rxjs';
import { environment } from '@env/environment';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { AuthResponse } from '@core/models/auth.model';
import { AuthStateManager } from '@features/auth/services/auth-state-manager-service';
import { TokenManager } from '@core/utils/token-manager';
import { Participant } from '@core/models/participant.model';
import { SweetAlertService } from '@core/services/sweet-alert.service';
import { FoodVenueService } from '../../food-venue/services/food-venue.service';
import { MenuService } from './menu-service';

import { logger } from '@core/utils/logger';
@Injectable({ providedIn: 'root' })
export class ClientTableSessionService {
  private authService = inject(AuthService);
  private authState = inject(AuthStateManager);
  private profileService = inject(ProfileService);
  private sseService = inject(ServerSentEventsService);
  private menuService = inject(MenuService);
  private http = inject(HttpClient);
  private sweetAlertService = inject(SweetAlertService);
  private foodVenueService = inject(FoodVenueService);
  private _isLoading = signal<boolean>(true);

  private _tableNumber = signal<number>(this.getStoredNumber('tableNumber'));
  private _participantNickname = signal<string>(this.getStoredString('participantNickname'));
  private _participantCount = signal<number>(0);
  private _tableCapacity = signal<number | null>(this.getStoredCapacity());
  private _activeParticipants = signal<Participant[]>([]);
  private _previousParticipants = signal<Participant[]>([]);
  private _hostParticipantId = signal<string | null>(null);

  private sseSubscription: Subscription | undefined;

  tableSessionInfo = computed<TableSessionInfo>(() => ({
    tableNumber: this._tableNumber(),
    participantNickname: this._participantNickname(),
    participantId: this.authState.participantId() || '',
    participantCount: this._participantCount(),
    sessionId: this.authService.tableSessionId(),
    tableCapacity: this._tableCapacity(),
    activeParticipants: this._activeParticipants(),
    previousParticipants: this._previousParticipants(),
    hostParticipantId: this._hostParticipantId(),
  }));

  hasActiveSession = computed(() => {
    const sessionId = this.authService.tableSessionId();
    return SessionUtils.isValidSession(sessionId);
  });

  sessionId = computed(() => this.authService.tableSessionId());
  myParticipantId = computed(() => this.authState.participantId() || '');

  isLoading = computed(() => this._isLoading());

  constructor() {
    effect(() => {
      if (this.hasActiveSession() && !this.authState.isGuest()) {
        this.syncNicknameFromProfile();
      }
      if (!this.hasActiveSession()) {
        this._isLoading.set(false);
      }
    });

    effect(() => {
      const hasSession = this.hasActiveSession();
      const isGuest = this.authState.isGuest();

      if (hasSession && !isGuest) {
        this._isLoading.set(true);
        this.recoverActiveSession().subscribe({
          next: (data: TableSession) => {
            this._isLoading.set(false);

            const myId = this.authState.participantId();
            const me = data.activeParticipants?.find((p: Participant) => p.publicId === myId);
            if (me) {
              this.updateNickname(me.nickname);
            }
          },
          error: (err) => {
            logger.warn('Mesa', 'No se pudo sincronizar la mesa después del login', err);
            this._isLoading.set(false);
          },
        });
      }
    });

    effect((onCleanup) => {
      const tableSessionId = this.sessionId();
      const hasSession = SessionUtils.isValidSession(tableSessionId);

      if (!hasSession || !tableSessionId) {
        if (this.sseSubscription) {
          this.sseSubscription.unsubscribe();
          this.sseSubscription = undefined;
        }
        return;
      }
      if (this.sseSubscription) return;

      this.sseSubscription = this.sseService.subscribeToSession().subscribe({
        next: (event) => {
          if (event.type === 'count-updated') {
            this._participantCount.set(event.payload.count);
          }

          if (event.type === 'user-joined') {
            const newParticipant = event.payload.participant;

            this._previousParticipants.update((prev) =>
              prev.filter((x) => x.publicId !== newParticipant.publicId),
            );
            this._activeParticipants.update((list) => [...list, newParticipant]);

            this.sweetAlertService.showToast(
              'top-end',
              'info',
              newParticipant.nickname + ' se unió a la mesa',
            );
          }

          if (event.type === 'user-left') {
            const leavingParticipant = event.payload.participant;

            if (leavingParticipant) {
              this._activeParticipants.update((active) =>
                active.filter((p) => p.publicId !== leavingParticipant.publicId),
              );

              const participantWithTime = {
                ...leavingParticipant,
                leftAt: new Date().toISOString(),
              };

              this._previousParticipants.update((prev) => {
                const filtered = prev.filter((x) => x.publicId !== participantWithTime.publicId);
                return [...filtered, participantWithTime];
              });

              if (leavingParticipant.publicId !== this.authState.participantId()) {
                this.sweetAlertService.showToast(
                  'top-end',
                  'info',
                  leavingParticipant.nickname + ' abandonó la mesa',
                );
              }
            }
          }

          if (event.type === 'host-delegated') {
            const newHost = event.payload.host;

            this._hostParticipantId.set(newHost.publicId);

            this._activeParticipants.update((participants) =>
              participants.map((p) => ({
                ...p,
                ...(p.publicId === newHost.publicId ? newHost : p),
              })),
            );
            if (newHost.publicId === this.authState.participantId()) {
              this.sweetAlertService.showInfo(
                'Fuiste designado como Anfitrión',
                'Sos el nuevo encargado de la mesa',
              );
            } else {
              this.sweetAlertService.showToast(
                'top-end',
                'info',
                `El nuevo anfitrión es ${newHost.nickname}`,
              );
            }
          }

          // El staff movió la mesa (antes nadie escuchaba este evento y el cliente seguía viendo la mesa vieja)
          if (event.type === 'reassign-table') {
            const newTableNumber = event.payload.newTableNumber;
            this._tableNumber.set(newTableNumber);
            localStorage.setItem('tableNumber', newTableNumber.toString());
            this.sweetAlertService.showToast(
              'top-end',
              'info',
              `Ahora están en la mesa ${newTableNumber}`,
            );
          }

          if (event.type === 'migrated-guest-session') {
            const updatedParticipant = event.payload.participant;
            const deletedIds = event.payload.deletedParticipantIds;
            const previousParticipant = this._activeParticipants().find(
              (p) => p.publicId === updatedParticipant.publicId,
            );
            const previousNickname = previousParticipant?.nickname;

            this._previousParticipants.update((prev) =>
              prev.filter(
                (p) =>
                  p.publicId !== updatedParticipant.publicId && !deletedIds.includes(p.publicId),
              ),
            );

            this._activeParticipants.update((active) => {
              let next = active.filter((p) => !deletedIds.includes(p.publicId));

              const idx = next.findIndex((p) => p.publicId === updatedParticipant.publicId);
              if (idx >= 0) {
                next = next.map((p) =>
                  p.publicId === updatedParticipant.publicId ? { ...p, ...updatedParticipant } : p,
                );
              } else {
                next = [...next, updatedParticipant];
              }
              return next;
            });
            if (updatedParticipant.publicId === this.authState.participantId()) {
              this.updateNickname(updatedParticipant.nickname);
            }
            // Si el absorbido era el anfitrión, el back se lo pasa al que queda
            const hostId = this._hostParticipantId();
            if (hostId && deletedIds.includes(hostId)) {
              this._hostParticipantId.set(updatedParticipant.publicId);
            }
            this.sweetAlertService.showToast(
              'top-end',
              'info',
              previousNickname && previousNickname !== updatedParticipant.nickname
                ? `${previousNickname} ahora está registrado como ${updatedParticipant.nickname}`
                : `${updatedParticipant.nickname} ahora está registrado`,
            );
          }
        },
        error: (err) =>
          logger.error('Mesa', `Error en la conexión en tiempo real: mesa=${tableSessionId}`, err),
      });

      onCleanup(() => {
        if (this.sseSubscription) {
          this.sseSubscription.unsubscribe();
          this.sseSubscription = undefined;
        }
      });
    });

    effect(() => {
      const hasSession = this.hasActiveSession();

      const currentTable = untracked(this._tableNumber);
      const currentParticipants = untracked(this._activeParticipants);

      if (hasSession && (currentTable === 0 || currentParticipants.length === 0)) {
        this._isLoading.set(true);

        this.recoverActiveSession().subscribe({
          next: () => {
            this._isLoading.set(false);
          },
          error: (err) => {
            logger.error('Mesa', 'Error al auto-recuperar sesión', err);
            this._isLoading.set(false);
          },
        });
      } else if (hasSession && currentTable > 0 && currentParticipants.length > 0) {
        this._isLoading.set(false);
      }
    });
  }

  private syncNicknameFromProfile(): void {
    this.profileService.getUserProfile().subscribe({
      next: (profile) => {
        const currentNickname = this._participantNickname();
        const profileName = profile.name || 'Usuario';

        if (currentNickname !== profileName) {
          this._participantNickname.set(profileName);
          localStorage.setItem('participantNickname', profileName);
        }
      },
      error: (error) => {
        logger.warn('Mesa', 'No se pudo sincronizar nickname desde perfil', error);
      },
    });
  }

  refreshNickname(): void {
    if (this.hasActiveSession() && !this.authState.isGuest()) {
      this.syncNicknameFromProfile();
    }
  }

  updateNickname(newNickname: string): void {
    if (newNickname && newNickname.trim()) {
      this._participantNickname.set(newNickname);
      localStorage.setItem('participantNickname', newNickname);
    }
  }

  recoverActiveSession(): Observable<TableSession> {
    return this.http.get<TableSession>(`${environment.apiUrl}/participants/table-sessions`).pipe(
      tap((response) => {
        const myParticipantId = this.authState.participantId();

        const me = response.activeParticipants?.find(
          (p: Participant) => p.publicId === myParticipantId,
        );

        let nicknameToSet = '';

        if (me && me.nickname) {
          nicknameToSet = me.nickname;
        } else {
          logger.warn('Mesa', 'El participante no está en la lista: se usan los datos guardados');
          nicknameToSet = this._participantNickname();
        }

        const count = response.numberOfParticipants ?? 1;

        const activeList = response.activeParticipants || [];
        const previousList = response.previousParticipants || [];
        const hostId = response.sessionHost?.publicId || null;

        this.setTableSessionInfo(
          response.tableNumber,
          nicknameToSet,
          count,
          response.tableCapacity || null,
          activeList,
          previousList,
          hostId,
        );
      }),
    );
  }

  setTableSessionInfo(
    tableNumber: number,
    participantNickname: string,
    participantCount: number,
    tableCapacity: number | null,
    activeParticipants: Participant[] = [],
    previousParticipants: Participant[] = [],
    hostParticipantId: string | null = null,
  ): void {
    if (tableNumber > 0) {
      this._tableNumber.set(tableNumber);
      localStorage.setItem('tableNumber', tableNumber.toString());
    } else {
      this._tableNumber.set(0);
      localStorage.removeItem('tableNumber');
    }

    if (participantNickname && participantNickname.trim()) {
      this._participantNickname.set(participantNickname);
      localStorage.setItem('participantNickname', participantNickname);
    } else {
      this._participantNickname.set('');
      localStorage.removeItem('participantNickname');
    }

    if (participantCount >= 0) {
      this._participantCount.set(participantCount);
    } else {
      this._participantCount.set(0);
    }

    if (tableCapacity === null) {
      this._tableCapacity.set(null);
      localStorage.removeItem('tableCapacity');
    } else if (typeof tableCapacity === 'number' && tableCapacity >= 0) {
      this._tableCapacity.set(tableCapacity);
      localStorage.setItem('tableCapacity', tableCapacity.toString());
    } else {
      this._tableCapacity.set(null);
      localStorage.removeItem('tableCapacity');
    }

    this._activeParticipants.set(activeParticipants);
    this._previousParticipants.set(previousParticipants);
    this._hostParticipantId.set(hostParticipantId);
  }

  private clearAllClientState() {
    this.clearSession();
    this.authState.clearSessionData();
    this.foodVenueService.clearAll();
    this.menuService.clearCache();
  }

  clearSession(): void {
    this._tableNumber.set(0);
    this._participantNickname.set('');
    this._participantCount.set(0);
    this._tableCapacity.set(null);
    this._activeParticipants.set([]);
    this._previousParticipants.set([]);

    localStorage.removeItem('tableNumber');
    localStorage.removeItem('participantNickname');
    localStorage.removeItem('tableCapacity');
  }

  //Esto podria estar en un servicio dedicado al participante actual
  closeSession(): Observable<HttpResponse<AuthResponse>> {
    return this.http
      .patch<AuthResponse>(`${environment.apiUrl}/participants/end`, null, {
        observe: 'response',
      })
      .pipe(
        tap((response) => {
          if (response.status === 200) {
            const processed = TokenManager.processResponse(response.body!);
            this.authState.applyAuthData(processed);
          } else {
            this.authState.clearState();
          }

          this.clearAllClientState();
        }),
      );
  }

  leaveSession(): Observable<HttpResponse<unknown>> {
    return this.http
      .patch<unknown>(`${environment.apiUrl}/participants/leave`, null, {
        observe: 'response',
      })
      .pipe(
        tap(() => {
          this.clearAllClientState();
        }),
      );
  }

  delegateHostingDuties(targetParticipantId: string): Observable<void> {
    return this.http.patch<void>(
      `${environment.apiUrl}/participants/host/${targetParticipantId}`,
      {},
    );
  }

  private getStoredNumber(key: string): number {
    try {
      const stored = localStorage.getItem(key);
      if (!stored) return 0;

      const parsed = parseInt(stored, 10);
      return isNaN(parsed) ? 0 : parsed;
    } catch (error) {
      logger.error('Mesa', `No se pudo leer un número guardado: clave=${key}`, error);
      return 0;
    }
  }

  private getStoredString(key: string): string {
    try {
      const stored = localStorage.getItem(key);
      return stored && stored.trim() ? stored : '';
    } catch (error) {
      logger.error('Mesa', `No se pudo leer un texto guardado: clave=${key}`, error);
      return '';
    }
  }

  private getStoredCapacity(): number | null {
    try {
      const stored = localStorage.getItem('tableCapacity');
      if (!stored) return null;

      if (stored === 'null') return null;

      const parsed = parseInt(stored, 10);
      return isNaN(parsed) ? null : parsed;
    } catch {
      return null;
    }
  }
}
